import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";

export interface ProdutoComissaoRow {
  id: number;
  perfilId: number;
  perfilNome?: string | null;
  tipoValorPercentual: number; // 0 = Percentual, 1 = Valor
  precoPadrao?: number | null;
  precoPromo1?: number | null;
  precoPromo2?: number | null;
  precoPromo3?: number | null;
  precoAtacado?: number | null;
  precoQuantidade?: number | null;
  precoQuantidadeAte?: number | null;
}

export type SalvarProdutoComissaoRequest = Omit<ProdutoComissaoRow, "id" | "perfilNome">;

export interface PerfilCombo {
  id: number;
  nome: string;
}

export const usePerfisCombo = () =>
  useQuery({
    queryKey: ["perfis-combo"],
    queryFn: async () => (await apiClient.get<PerfilCombo[]>("/api/perfis")).data,
  });

export const useProdutoComissoes = (produtoId: string | undefined) => {
  const qc = useQueryClient();
  const numId = produtoId ? Number(produtoId) : undefined;
  const key = ["produto-comissoes", numId];
  const base = `/api/produtos/${numId}/comissoes`;

  const query = useQuery({
    queryKey: key,
    queryFn: async () => (await apiClient.get<ProdutoComissaoRow[]>(base)).data,
    enabled: !!numId,
  });

  const create = useMutation({
    mutationFn: async (body: SalvarProdutoComissaoRequest) => (await apiClient.post<ProdutoComissaoRow>(base, body)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); toast.success("Regra de comissão adicionada!"); },
    onError: (e: Error) => toast.error(`Erro: ${e.message}`),
  });
  const update = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: SalvarProdutoComissaoRequest }) =>
      (await apiClient.put<ProdutoComissaoRow>(`${base}/${id}`, data)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); toast.success("Regra de comissão atualizada!"); },
    onError: (e: Error) => toast.error(`Erro: ${e.message}`),
  });
  const remove = useMutation({
    mutationFn: async (id: number) => { await apiClient.delete(`${base}/${id}`); },
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); toast.success("Regra de comissão removida!"); },
    onError: (e: Error) => toast.error(`Erro: ${e.message}`),
  });

  return {
    comissoes: query.data ?? [],
    createComissao: create.mutate,
    updateComissao: update.mutate,
    deleteComissao: remove.mutate,
  };
};
